import { ApolloError } from '@apollo/client';
import { type ErrorResponse, onError } from '@apollo/client/link/error';
import type { ErrorKind } from '@shared/errors';
import type { GraphQLFormattedError } from 'graphql';
import { createElement } from 'react';

import ErrorToastActions from 'src/components/atoms/ErrorToastActions';
import { type NotifyOptions, notify, toUserMessage } from 'src/lib/notify';
import type { ApolloErrorContext } from './errorPresentation';

// ---------------------------------------------------------------------------
// Apollo Link — surface typed AppError GraphQL extensions as notifications
// ---------------------------------------------------------------------------

/** Composed ABOVE the 401 link so it observes the FINAL post-retry response — a transient 401 that recovers never toasts; `networkError` toasts are THROTTLED to one per window. */

/** kinds whose message is a user warning; anything else is a hard error toast. */
const WARN_KINDS: ReadonlySet<string> = new Set<ErrorKind>(['validation', 'not_found', 'conflict', 'auth']);

/** Taxonomy fields this link reads off `extensions`. `kind` stays `string` — untrusted wire data validated only for choosing the notify level. `extensions.field` is deliberately not read until inline routing lands. */
interface AppErrorExtensions {
  code: string;
  kind: string;
  suggestion?: string;
  referenceSlug?: string;
  traceId?: string;
}

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Reads our taxonomy off `extensions`, or `null` when the error is not ours
 *  (no `code` → a non-taxonomy error → this link stays inert). */
function parseAppErrorExtensions(extensions: GraphQLFormattedError['extensions']): AppErrorExtensions | null {
  if (!extensions) return null;
  const code = asNonEmptyString(extensions.code);
  if (!code) return null;

  return {
    code,
    kind: asNonEmptyString(extensions.kind) ?? '',
    suggestion: asNonEmptyString(extensions.suggestion),
    referenceSlug: asNonEmptyString(extensions.referenceSlug),
    traceId: asNonEmptyString(extensions.traceId),
  };
}

/** Unknown/system/integration kinds → `error`; user-tier kinds → `warn`. An
 *  unrecognized kind defaults to `error` so a mis-tagged failure is never
 *  silently downgraded. */
function levelForKind(kind: string): 'error' | 'warn' {
  return WARN_KINDS.has(kind) ? 'warn' : 'error';
}

/** message + suggestion (whichever the backend provided). */
function composeMessage(message: string, extensions: AppErrorExtensions): string {
  return extensions.suggestion ? `${message} ${extensions.suggestion}` : message;
}

/** The backend gates `referenceSlug` via `isReferenceVisible`, so a toast that has one gets the copy chip and a
 *  Contact support link; a toast without one gets no actions. */
function toastOptions({ referenceSlug, traceId }: AppErrorExtensions): NotifyOptions | undefined {
  if (!referenceSlug) return undefined;
  return { action: createElement(ErrorToastActions, { referenceSlug, traceId }) };
}

/** Surfaces a single GraphQL error via `notify.*` when it carries our taxonomy; returns `true` if it notified. Field-scoped errors (`extensions.field`) fall back to a toast until inline routing is built. */
export function surfaceAppError(error: GraphQLFormattedError): boolean {
  const extensions = parseAppErrorExtensions(error.extensions);
  if (!extensions) return false;

  const message = composeMessage(error.message, extensions);
  const notifyAtLevel = levelForKind(extensions.kind) === 'error' ? notify.error : notify.warn;
  const options = toastOptions(extensions);
  if (options) notifyAtLevel(message, options);
  else notifyAtLevel(message);
  return true;
}

/** Typed AppErrors use this link's presentation policy, including inline and internal handling.
 * This classifies the error; it does not prove a toast was shown. */
export function wasSurfacedByAppErrorLink(error: unknown): boolean {
  return (
    error instanceof ApolloError &&
    error.graphQLErrors.some((graphQLError) => parseAppErrorExtensions(graphQLError.extensions) !== null)
  );
}

/** Show an actionable caller's fallback unless the shared link owns the error's presentation. */
export function notifyUnlessSurfaced(error: unknown, failure: string): void {
  if (wasSurfacedByAppErrorLink(error)) return;
  if (error instanceof ApolloError && error.networkError) return;
  notify.error(`${failure} ${toUserMessage(error)}`);
}

/** Shown for a transport failure that carries no GraphQL body (500 / timeout /
 *  offline). Deliberately generic so no internal detail leaks. */
const NETWORK_ERROR_MESSAGE = 'Could not reach the server. Check your connection and try again.';
/** A genuinely dead session surfaces here as a 401 — the 401 replay link only retries, it never resolves a dead session — so it gets a sign-in message rather than a misleading "check your connection". */
const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

/** Offline / server-down failures arrive once PER failing operation (background
 *  refetches included), so one offline moment would fire a storm of identical
 *  toasts. Show at most one network toast per this window. */
const NETWORK_TOAST_THROTTLE_MS = 5_000;
let lastNetworkToastAt = 0;

function networkErrorMessage(networkError: NonNullable<ErrorResponse['networkError']>): string {
  if ('statusCode' in networkError && networkError.statusCode === 401) return SESSION_EXPIRED_MESSAGE;
  return NETWORK_ERROR_MESSAGE;
}

/** The subset of Apollo's `ErrorResponse` this callback reads. */
type ApolloErrorOperation = Pick<ErrorResponse['operation'], 'operationName' | 'getContext'>;
type ApolloErrorInput = Pick<ErrorResponse, 'graphQLErrors' | 'networkError'> & { operation?: ApolloErrorOperation };

function errorPresentation(
  operation: ApolloErrorOperation | undefined
): ApolloErrorContext['appErrorPresentation'] | undefined {
  const presentation = operation?.getContext().appErrorPresentation;
  return presentation === 'inline' || presentation === 'internal' ? presentation : undefined;
}

/** Core of the Apollo `onError` callback. Explicit inline/internal contexts log safe metadata and leave presentation to their owner. Other GraphQL errors surface via the taxonomy (self-gating: no `extensions.code` ⇒ inert). A bare `networkError` carries no taxonomy, so it gets a generic toast, throttled so an offline burst yields one. */
export function surfaceApolloError({ graphQLErrors, networkError, operation }: ApolloErrorInput): void {
  const presentation = errorPresentation(operation);
  if (presentation) {
    console.warn(
      {
        operationName: operation?.operationName,
        presentation,
        errors: graphQLErrors?.map((error) => {
          const taxonomy = parseAppErrorExtensions(error.extensions);
          return { code: taxonomy?.code, kind: taxonomy?.kind };
        }),
        networkError: Boolean(networkError),
        statusCode:
          networkError && 'statusCode' in networkError && typeof networkError.statusCode === 'number'
            ? networkError.statusCode
            : undefined,
      },
      '[Apollo] Operation failed'
    );
    return;
  }
  if (graphQLErrors?.length) {
    for (const error of graphQLErrors) surfaceAppError(error);
    return;
  }
  if (!networkError) return;

  const now = Date.now();
  if (now - lastNetworkToastAt < NETWORK_TOAST_THROTTLE_MS) return;
  lastNetworkToastAt = now;
  notify.error(networkErrorMessage(networkError));
}

export const appErrorLink = onError(surfaceApolloError);
